import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import Onboarding from "./Onboarding";
import AuthScreen from "./AuthScreen";
import InviteSheet from "./InviteSheet";
import DiningTogether from "./DiningTogether";
import { supabase } from "./supabase";

// Fire-and-forget usage event logging — never blocks the UI, never throws
function logUsageEvent(userId, eventType, metadata = {}) {
  if (!userId) return;
  supabase
    .from("usage_events")
    .insert({ user_id: userId, event_type: eventType, metadata })
    .then(({ error }) => { if (error) console.error("Usage event log error:", error); });
}

// Records the tokens one Claude call used, so spend can be measured per call and per user.
// The backend returns usage: { model, input_tokens, output_tokens } on every successful call.
function logClaudeUsage(userId, route, data) {
  if (!data || !data.usage) return;
  logUsageEvent(userId, "claude_usage", { route, ...data.usage });
}

// Every call to the Davino server goes through here: it attaches the signed-in user's
// access token so the server can confirm the request comes from a real Davino account
// (otherwise anyone who found the URL could run up the Anthropic bill). getSession()
// refreshes the token if it has expired. A 401 means the session is no longer valid —
// sign out cleanly so the user lands back on the sign-in screen rather than a broken app.
const API_BASE = "https://davino-production.up.railway.app";

async function apiFetch(path, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { "Content-Type": "application/json" };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body)
  });
  if (response.status === 401) {
    supabase.auth.signOut().catch(() => {});
  }
  return response;
}

// One attempt at the wine-list extraction call. Throws with a short reason string on any
// failure — network drop, a non-JSON response, or a 200 that came back without wineList
// (the backend's own catch-all error case) — so the caller can retry or log consistently
// regardless of which of those actually happened.
async function attemptWineListFetch(imageBase64, mediaType, userId) {
  let response;
  try {
    response = await apiFetch("/extract-wine-list", { imageBase64, mediaType });
  } catch (err) {
    throw new Error("network");
  }
  let data;
  try {
    data = await response.json();
  } catch (err) {
    throw new Error("bad_response");
  }
  if (!data.wineList) {
    throw new Error(data.error ? `server: ${data.error}` : "no_wine_list");
  }
  logClaudeUsage(userId, "extract-wine-list", data);
  return data.wineList;
}

const styles = {
  wrapper: {
    backgroundColor: "#111",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center"
  },
  app: {
    backgroundColor: "#111",
    minHeight: "100vh",
    color: "#f0f0f0",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    width: "100%",
    maxWidth: "480px"
  },
  header: {
    padding: "24px 20px 16px",
    borderBottom: "1px solid #222"
  },
  logo: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#fff",
    letterSpacing: "-0.5px"
  },
  logoAccent: {
    color: "#8B4513"
  },
  tagline: {
    fontSize: "13px",
    color: "#666",
    marginTop: "4px"
  },
  tabBar: {
    display: "flex",
    borderBottom: "1px solid #222",
    backgroundColor: "#111"
  },
  tab: {
    flex: 1,
    padding: "12px",
    background: "none",
    border: "none",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    fontFamily: "inherit",
    color: "#555",
    borderBottom: "2px solid transparent"
  },
  tabActive: {
    color: "#8B4513",
    borderBottom: "2px solid #8B4513"
  },
  section: {
    padding: "20px"
  },
  label: {
    fontSize: "13px",
    color: "#888",
    marginBottom: "8px",
    display: "block",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  textarea: {
    width: "100%",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    color: "#f0f0f0",
    fontSize: "15px",
    padding: "14px",
    resize: "vertical",
    minHeight: "160px",
    fontFamily: "inherit",
    lineHeight: "1.5",
    boxSizing: "border-box"
  },
  contextInput: {
    width: "100%",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    color: "#f0f0f0",
    fontSize: "15px",
    padding: "14px",
    fontFamily: "inherit",
    boxSizing: "border-box"
  },
  button: {
    width: "100%",
    backgroundColor: "#8B4513",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "17px",
    fontWeight: "600",
    padding: "16px",
    cursor: "pointer",
    marginTop: "16px"
  },
  buttonDisabled: {
    backgroundColor: "#3a2a20",
    color: "#666",
    cursor: "not-allowed"
  },
  loadingText: {
    textAlign: "center",
    color: "#888",
    fontSize: "15px",
    padding: "32px 20px",
    lineHeight: "1.8"
  },
  recommendationBox: {
    margin: "0 20px 32px",
    backgroundColor: "#1a1a1a",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #2a2a2a"
  },
  recommendationTitle: {
    fontSize: "13px",
    color: "#8B4513",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "16px",
    fontWeight: "600"
  },
  recommendationText: {
    fontSize: "16px",
    lineHeight: "1.7",
    color: "#e0e0e0"
  },
  error: {
    margin: "0 20px",
    padding: "14px",
    backgroundColor: "#2a1a1a",
    borderRadius: "12px",
    color: "#ff6b6b",
    fontSize: "14px"
  },
  divider: {
    height: "1px",
    backgroundColor: "#1e1e1e",
    margin: "0 0 20px 0"
  },
  feedbackBox: {
    margin: "0 20px 40px",
    backgroundColor: "#1a1a1a",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #2a2a2a"
  },
  feedbackTitle: {
    fontSize: "13px",
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "14px",
    fontWeight: "600"
  },
  feedbackQuestion: {
    fontSize: "16px",
    color: "#f0f0f0",
    marginBottom: "16px",
    fontWeight: "500"
  },
  wineOptionButton: {
    width: "100%",
    backgroundColor: "#222",
    border: "1px solid #333",
    borderRadius: "10px",
    color: "#e0e0e0",
    fontSize: "14px",
    padding: "12px 14px",
    cursor: "pointer",
    marginBottom: "8px",
    textAlign: "left",
    fontFamily: "inherit"
  },
  somethingElseButton: {
    width: "100%",
    backgroundColor: "transparent",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    color: "#888",
    fontSize: "14px",
    padding: "12px 14px",
    cursor: "pointer",
    marginBottom: "8px",
    textAlign: "left",
    fontFamily: "inherit"
  },
  ratingButton: {
    width: "100%",
    backgroundColor: "#222",
    border: "1px solid #333",
    borderRadius: "10px",
    color: "#e0e0e0",
    fontSize: "14px",
    padding: "13px 14px",
    cursor: "pointer",
    marginBottom: "8px",
    textAlign: "left",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  ratingNumber: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#8B4513",
    minWidth: "16px"
  },
  feedbackNoteInput: {
    width: "100%",
    backgroundColor: "#222",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    color: "#f0f0f0",
    fontSize: "15px",
    padding: "12px 14px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    marginBottom: "12px"
  },
  feedbackSubmitButton: {
    width: "100%",
    backgroundColor: "#8B4513",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    padding: "13px",
    cursor: "pointer",
    fontFamily: "inherit"
  },
  inviteHeaderButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "4px",
    background: "none",
    border: "1px solid #3a3a3a",
    borderRadius: "999px",
    color: "#ccc",
    fontSize: "14px",
    fontWeight: "600",
    padding: "7px 14px",
    cursor: "pointer",
    fontFamily: "inherit"
  },
  inviteNudge: {
    display: "block",
    width: "100%",
    background: "none",
    border: "none",
    color: "#999",
    fontSize: "14px",
    padding: "0 0 14px",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "center"
  },
  feedbackDone: {
    textAlign: "center",
    color: "#888",
    fontSize: "15px",
    padding: "8px 0",
    marginBottom: "16px"
  },
  nextStepPrimary: {
    width: "100%",
    backgroundColor: "#8B4513",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    padding: "13px",
    cursor: "pointer",
    fontFamily: "inherit",
    marginBottom: "8px"
  },
  nextStepSecondary: {
    width: "100%",
    backgroundColor: "transparent",
    color: "#aaa",
    border: "1px solid #333",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "500",
    padding: "12px",
    cursor: "pointer",
    fontFamily: "inherit",
    marginBottom: "8px"
  },
  // History styles
  historySection: {
    padding: "20px"
  },
  historyEmpty: {
    textAlign: "center",
    color: "#555",
    fontSize: "15px",
    lineHeight: "1.7",
    paddingTop: "48px"
  },
  historyToggle: {
    display: "flex",
    gap: "8px",
    marginBottom: "20px"
  },
  historyToggleBtn: {
    padding: "6px 14px",
    borderRadius: "20px",
    border: "1px solid #333",
    backgroundColor: "transparent",
    color: "#888",
    fontSize: "13px",
    cursor: "pointer",
    fontFamily: "inherit"
  },
  historyToggleBtnActive: {
    backgroundColor: "#8B4513",
    borderColor: "#8B4513",
    color: "#fff"
  },
  historyCard: {
    backgroundColor: "#1a1a1a",
    borderRadius: "14px",
    padding: "16px",
    marginBottom: "12px",
    border: "1px solid #2a2a2a"
  },
  historyWineName: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#f0f0f0",
    marginBottom: "6px",
    lineHeight: "1.4"
  },
  historyMeta: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "6px"
  },
  historyRating: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#8B4513"
  },
  historyRatingLabel: {
    fontSize: "13px",
    color: "#888"
  },
  historyDate: {
    fontSize: "12px",
    color: "#555",
    marginLeft: "auto"
  },
  historyNote: {
    fontSize: "13px",
    color: "#777",
    fontStyle: "italic",
    marginTop: "4px"
  }
};

const RATING_OPTIONS = [
  { value: 1, label: "Hard pass" },
  { value: 2, label: "Drinkable if no other options" },
  { value: 3, label: "Good — would order again" },
  { value: 4, label: "Seeking this one out" }
];

const LOADING_MESSAGES = [
  "Reading your wine list",
  "Matching to your palate",
  "Almost there"
];

const SCAN_MESSAGES = [
  "Reading the wine list",
  "Identifying the wines",
  "Almost done"
];

const markdownComponents = {
  p: ({ children }) => (
    <p style={{ margin: "0 0 14px 0", lineHeight: "1.7" }}>{children}</p>
  ),
  strong: ({ children }) => {
    const text = Array.isArray(children) ? children.join("") : String(children ?? "");
    return (
      <strong style={{ color: "#fff", fontWeight: "600" }}>
        {children}
        {isLikelyWineName(text) && <CopyWineButton text={text} />}
      </strong>
    );
  },
  hr: () => (
    <hr style={{ border: "none", borderTop: "1px solid #2a2a2a", margin: "16px 0" }} />
  )
};

const SECTION_HEADERS = new Set(["whites", "reds", "sparkling", "rosé", "rose", "orange", "pink", "skip", "dessert"]);

// Shared heuristic: is this bolded phrase a wine name (vs. a section header or
// stray emphasis)? Used both to build the "what did you order" picker and to
// decide where to show the copy-to-clipboard icon inline in the recommendation text.
function isLikelyWineName(text) {
  const wine = text.trim();
  if (!wine || SECTION_HEADERS.has(wine.toLowerCase())) return false;
  return (
    wine.includes("—") ||
    wine.includes(" - ") ||
    wine.match(/\$\d+/) ||
    wine.match(/\b(19|20)\d{2}\b/) ||
    wine.length > 15
  );
}

function parseRecommendedWines(text) {
  const matches = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const wine = match[1].trim();
    if (isLikelyWineName(wine)) matches.push(wine);
  }
  return matches;
}

// Small inline "copy this wine name" affordance — no explanation of what it's for,
// just a familiar copy icon for anyone who wants to paste it into CellarTracker,
// Vivino, or wherever else they'd look a wine up themselves.
function CopyWineButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
      }}
      aria-label={`Copy "${text}"`}
      title="Copy wine name"
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "6px 6px 6px 10px",
        margin: "-6px 0",
        verticalAlign: "middle",
        display: "inline-flex",
        opacity: 0.8
      }}
    >
      {copied ? (
        <svg width="19" height="19" viewBox="0 0 16 16" fill="none">
          <path d="M3 8.5L6.5 12L13 4.5" stroke="#8B4513" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="19" height="19" viewBox="0 0 16 16" fill="none">
          <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="#ccc" strokeWidth="1.4" />
          <path d="M3.5 10.5V3.5C3.5 2.94772 3.94772 2.5 4.5 2.5H10.5" stroke="#ccc" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      )}
    </button>
  );
}

function formatHistoryDate(dateStr) {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function App() {
  const [session, setSession] = useState(null);
  const sessionRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [appReady, setAppReady] = useState(false);
  const [currentView, setCurrentView] = useState("recommend");
  const [showInvite, setShowInvite] = useState(false);

  // Dine together: taste profiles of the people sharing the bottle tonight ([{ name, profile }])
  const [companions, setCompanions] = useState([]);
  const [recCompanionNames, setRecCompanionNames] = useState([]);
  // Arriving from a pairing link (davino.ai/?pair=CODE) — grab the code, then tidy the URL
  const [pairCodeFromLink] = useState(() => {
    try {
      const code = new URLSearchParams(window.location.search).get("pair");
      if (code) window.history.replaceState(null, "", window.location.pathname);
      return code;
    } catch (e) { return null; }
  });
  const [wineList, setWineList] = useState("");
  const [sessionContext, setSessionContext] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [previousRecommendation, setPreviousRecommendation] = useState("");
  const [viewingPrevious, setViewingPrevious] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingDots, setLoadingDots] = useState(".");
  const [error, setError] = useState("");

  // Feedback state
  const [feedbackStep, setFeedbackStep] = useState(null);
  const [feedbackWine, setFeedbackWine] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [showSomethingElse, setShowSomethingElse] = useState(false);
  const [somethingElseText, setSomethingElseText] = useState("");
  const [comparisonCandidate, setComparisonCandidate] = useState(null);

  // Scan state
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState("");
  const [scanDots, setScanDots] = useState(".");
  const [scanError, setScanError] = useState("");
  const fileInputRef = useRef(null);

  // Session memory — wines already suggested this session
  const [sessionSuggestedWines, setSessionSuggestedWines] = useState([]);

  // Vintage report state
  const [vintageReport, setVintageReport] = useState(null);
  const [vintageLoading, setVintageLoading] = useState(false);
  const [showVintageReport, setShowVintageReport] = useState(false);
  const [vintageError, setVintageError] = useState("");

  // History state
  const [historyFilter, setHistoryFilter] = useState("fours"); // 'fours' | 'good' | 'all'

  // Resumed pending recommendation (from a prior visit that never got rated)
  const [isResumedPending, setIsResumedPending] = useState(false);
  const [pendingDateLabel, setPendingDateLabel] = useState("");
  const resumedPendingRef = useRef(false);

  // Persist (or clear, if pending is null) the "unrated recommendation" marker so that
  // reopening the app — same device or a different one, minutes or days later — can
  // resume the rating prompt instead of losing it when the tab reloads.
  function updatePendingRecommendation(pending) {
    const currentSession = sessionRef.current;
    setProfile(prev => {
      if (!prev) return prev;
      const updated = { ...prev, pending_recommendation: pending };
      try { localStorage.setItem("davino_profile_cache", JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
    if (currentSession) {
      supabase
        .from("profiles")
        .update({ pending_recommendation: pending })
        .eq("user_id", currentSession.user.id)
        .then(({ error }) => { if (error) console.error("Error saving pending recommendation:", error); });
    }
  }

  // On first load, resume an unrated recommendation from a prior visit, if one exists
  // and isn't too stale to be relevant.
  useEffect(() => {
    if (!profile || resumedPendingRef.current) return;
    resumedPendingRef.current = true;

    const pending = profile.pending_recommendation;
    if (!pending || !pending.recommendation) return;

    const PENDING_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
    const ageMs = Date.now() - new Date(pending.createdAt).getTime();
    if (Number.isNaN(ageMs) || ageMs > PENDING_MAX_AGE_MS) {
      updatePendingRecommendation(null); // too stale to be useful — clear quietly
      return;
    }

    setPendingDateLabel(new Date(pending.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
    setIsResumedPending(true);
    setRecommendation(pending.recommendation);
    setFeedbackStep("pick");
  }, [profile]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAppReady(true);
    }, 8000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        clearTimeout(timeout);
        setSession(session);
        sessionRef.current = session;
        if (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
          await initializeUser(session.user.id);
          logUsageEvent(session.user.id, "app_open");
        } else if (!session) {
          setAppReady(true);
        }
      }
    );

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function initializeUser(userId) {
    // Migrate old localStorage profile to Supabase if present
    try {
      const localData = localStorage.getItem("davino_profile");
      if (localData) {
        const localProfile = JSON.parse(localData);
        if (localProfile.profile_status === "complete") {
          await saveProfileToSupabase(userId, localProfile);
          localStorage.removeItem("davino_profile");
          setProfile(localProfile);
          setAppReady(true);
          return;
        }
      }
    } catch (e) {}

    // Load from Supabase
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) console.error("Supabase profile load error:", error);

      if (data && data.profile_status === "complete") {
        setProfile(data);
        // Keep cache fresh
        try {
          localStorage.setItem("davino_profile_cache", JSON.stringify(data));
        } catch (e) {}
        setAppReady(true);
        return;
      }
    } catch (e) {
      console.error("Error loading profile:", e);
    }

    // Supabase failed or returned nothing — try localStorage cache
    try {
      const cached = localStorage.getItem("davino_profile_cache");
      if (cached) {
        const cachedProfile = JSON.parse(cached);
        if (cachedProfile.profile_status === "complete") {
          setProfile(cachedProfile);
          setAppReady(true);
          return;
        }
      }
    } catch (e) {}

    setAppReady(true);
  }

  async function saveProfileToSupabase(userId, profileData) {
    // Always cache locally as a backup
    try {
      localStorage.setItem("davino_profile_cache", JSON.stringify(profileData));
    } catch (e) {}

    try {
      const { error } = await supabase
        .from("profiles")
        .upsert({
          user_id: userId,
          experience_level: profileData.experience_level,
          taste_dimensions: profileData.taste_dimensions,
          explicit_loves: profileData.explicit_loves,
          explicit_avoids: profileData.explicit_avoids,
          dietary: profileData.dietary,
          price_sensitivity: profileData.price_sensitivity,
          profile_notes: profileData.profile_notes,
          profile_status: profileData.profile_status,
          feedback_history: profileData.feedback_history || []
        }, { onConflict: "user_id" });
      if (error) console.error("Supabase upsert error:", error);
    } catch (e) {
      console.error("Error saving profile:", e);
    }
  }

  async function handleOnboardingComplete(profileData) {
    const currentSession = sessionRef.current;
    setProfile(profileData);
    if (currentSession) {
      saveProfileToSupabase(currentSession.user.id, profileData);
    } else {
      console.warn("No session at onboarding completion — profile not saved to Supabase");
    }
  }

  // Log a wine state
  const [logWineStep, setLogWineStep] = useState(null); // null | 'name' | 'rate' | 'note' | 'saved'
  const [logWineName, setLogWineName] = useState("");
  const [logWineRating, setLogWineRating] = useState(null);
  const [logWineNote, setLogWineNote] = useState("");
  const [logWineScanning, setLogWineScanning] = useState(false);
  const [logWineScanError, setLogWineScanError] = useState("");
  const logWineFileInputRef = useRef(null);

  function openInvite() {
    setShowInvite(true);
    logUsageEvent(sessionRef.current?.user?.id, "invite_opened");
  }

  function resetLogWine() {
    setLogWineStep(null);
    setLogWineName("");
    setLogWineRating(null);
    setLogWineNote("");
    setLogWineScanning(false);
    setLogWineScanError("");
  }

  const handleLogWineScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogWineScanning(true);
    setLogWineScanError("");
    try {
      const { imageBase64, mediaType } = await compressImage(file);
      const response = await apiFetch("/extract-wine-list", { imageBase64, mediaType });
      const data = await response.json();
      logClaudeUsage(sessionRef.current?.user?.id, "extract-wine-list", data);
      if (data.wineList) {
        setLogWineName(data.wineList.trim());
      } else {
        setLogWineScanError("Couldn't read the label. Try again or type the name.");
      }
    } catch (err) {
      setLogWineScanError("Couldn't connect. Try typing the name instead.");
    } finally {
      setLogWineScanning(false);
    }
    e.target.value = "";
  };

  async function handleLogWineSave() {
    const entry = {
      date: new Date().toISOString().split("T")[0],
      wine: logWineName.trim(),
      rating: logWineRating,
      note: logWineNote.trim() || null
    };

    const updatedHistory = [...(profile.feedback_history || []), entry];
    const updatedProfile = { ...profile, feedback_history: updatedHistory };
    setProfile(updatedProfile);
    try { localStorage.setItem("davino_profile_cache", JSON.stringify(updatedProfile)); } catch (e) {}

    setLogWineStep("saved");

    const currentSession = sessionRef.current;
    if (currentSession) {
      logUsageEvent(currentSession.user.id, "log_wine");

      supabase
        .from("profiles")
        .update({ feedback_history: updatedHistory })
        .eq("user_id", currentSession.user.id)
        .then(({ error }) => { if (error) console.error("Error saving logged wine:", error); });

      // Update taste dimensions in background (skip rating 3 — weak signal)
      if (logWineRating !== 3 && profile.taste_dimensions) {
        apiFetch("/update-dimensions", {
            wine: logWineName.trim(),
            rating: logWineRating,
            taste_dimensions: profile.taste_dimensions,
            experience_level: profile.experience_level
          })
          .then(r => r.json())
          .then(data => {
            logClaudeUsage(currentSession.user.id, "update-dimensions", data);
            if (data.taste_dimensions) {
              const refined = { ...updatedProfile, taste_dimensions: data.taste_dimensions };
              setProfile(refined);
              try { localStorage.setItem("davino_profile_cache", JSON.stringify(refined)); } catch (e) {}
              supabase
                .from("profiles")
                .update({ taste_dimensions: data.taste_dimensions })
                .eq("user_id", currentSession.user.id)
                .then(({ error }) => { if (error) console.error("Error saving dimensions:", error); });
            }
          })
          .catch(err => console.error("Dimension update error:", err));
      }
    }
  }

  function resetFeedback() {
    setFeedbackStep(null);
    setFeedbackWine("");
    setFeedbackRating(null);
    setFeedbackNote("");
    setShowSomethingElse(false);
    setSomethingElseText("");
    setComparisonCandidate(null);
  }

  function normalizeWineName(name) {
    return name.split("—")[0]           // drop price/extra info after em dash
      .replace(/[^a-zA-Z0-9]/g, "")    // strip punctuation and spaces
      .toLowerCase()
      .substring(0, 30);               // compare first 30 chars
  }

  function findComparisonCandidate(history, todayRating, todayWine) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const todayNorm = normalizeWineName(todayWine);

    const eligible = history.filter(entry => {
      if (entry.type === "comparison") return false;
      const entryDate = new Date(entry.date + "T12:00:00");
      const withinTime = entryDate >= thirtyDaysAgo;
      const withinRating = Math.abs(entry.rating - todayRating) <= 1;
      const notSameWine = normalizeWineName(entry.wine) !== todayNorm;
      return withinTime && withinRating && notSameWine;
    });

    if (eligible.length === 0) return null;
    return eligible[eligible.length - 1]; // most recent
  }

  function handleWinePick(wine) {
    setFeedbackWine(wine);
    setFeedbackStep("rate");
  }

  function handleSomethingElseSubmit() {
    if (!somethingElseText.trim()) return;
    setFeedbackWine(somethingElseText.trim());
    setFeedbackStep("rate");
  }

  function handleRating(value) {
    setFeedbackRating(value);
    setFeedbackStep("note");
  }

  async function handleFeedbackSubmit() {
    const entry = {
      date: new Date().toISOString().split("T")[0],
      wine: feedbackWine,
      rating: feedbackRating,
      note: feedbackNote.trim() || null
    };

    const updatedHistory = [...(profile.feedback_history || []), entry];

    // Update UI and cache immediately
    const updatedProfile = { ...profile, feedback_history: updatedHistory };
    setProfile(updatedProfile);
    try { localStorage.setItem("davino_profile_cache", JSON.stringify(updatedProfile)); } catch (e) {}

    // A rating was just captured — nothing left to nudge them about on next visit
    updatePendingRecommendation(null);
    setIsResumedPending(false);

    // Advance UI immediately — don't wait for Supabase
    const candidate = findComparisonCandidate(profile.feedback_history || [], feedbackRating, feedbackWine);
    if (candidate) {
      setComparisonCandidate(candidate);
      setFeedbackStep("compare");
    } else {
      setFeedbackStep("done");
    }

    // Save feedback history to Supabase in the background
    const currentSession = sessionRef.current;
    if (currentSession) {
      supabase
        .from("profiles")
        .update({ feedback_history: updatedHistory })
        .eq("user_id", currentSession.user.id)
        .then(({ error }) => { if (error) console.error("Error saving feedback:", error); });
    }

    // Update taste dimensions in the background (fire and forget)
    // Skip for rating 3 — weak signal not worth updating on
    if (feedbackRating !== 3 && profile.taste_dimensions && currentSession) {
      apiFetch("/update-dimensions", {
          wine: feedbackWine,
          rating: feedbackRating,
          taste_dimensions: profile.taste_dimensions,
          experience_level: profile.experience_level
        })
        .then(r => r.json())
        .then(data => {
          logClaudeUsage(currentSession.user.id, "update-dimensions", data);
          if (data.taste_dimensions) {
            const refined = { ...updatedProfile, taste_dimensions: data.taste_dimensions };
            setProfile(refined);
            try { localStorage.setItem("davino_profile_cache", JSON.stringify(refined)); } catch (e) {}
            supabase
              .from("profiles")
              .update({ taste_dimensions: data.taste_dimensions })
              .eq("user_id", currentSession.user.id)
              .then(({ error }) => { if (error) console.error("Error saving dimensions:", error); });
          }
        })
        .catch(err => console.error("Dimension update error:", err));
    }
  }

  async function handleComparisonChoice(winner, loser) {
    const compEntry = {
      type: "comparison",
      winner,
      loser,
      date: new Date().toISOString().split("T")[0]
    };

    const updatedHistory = [...(profile.feedback_history || []), compEntry];
    const updatedProfile = { ...profile, feedback_history: updatedHistory };
    setProfile(updatedProfile);
    try { localStorage.setItem("davino_profile_cache", JSON.stringify(updatedProfile)); } catch (e) {}

    setFeedbackStep("done");

    // Save to Supabase in the background (fire and forget)
    const currentSession = sessionRef.current;
    if (currentSession) {
      supabase
        .from("profiles")
        .update({ feedback_history: updatedHistory })
        .eq("user_id", currentSession.user.id)
        .then(({ error }) => { if (error) console.error("Error saving comparison:", error); });
    }
  }

  if (!appReady) return null;
  if (!session) return <AuthScreen />;
  if (!profile) return <Onboarding onComplete={handleOnboardingComplete} />;

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_DIMENSION = 1600;
          let { width, height } = img;

          // Scale down if either dimension exceeds MAX_DIMENSION
          if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
            if (width > height) {
              height = Math.round((height * MAX_DIMENSION) / width);
              width = MAX_DIMENSION;
            } else {
              width = Math.round((width * MAX_DIMENSION) / height);
              height = MAX_DIMENSION;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Start at 0.85 quality, reduce until under 4MB
          let quality = 0.85;
          let dataUrl = canvas.toDataURL("image/jpeg", quality);
          while (dataUrl.length > 5333333 && quality > 0.4) { // 5333333 ≈ 4MB in base64
            quality -= 0.1;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
          }

          const imageBase64 = dataUrl.split(",")[1];
          resolve({ imageBase64, mediaType: "image/jpeg" });
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  function handleNewWineList() {
    setWineList("");
    setSessionContext("");
    setRecommendation("");
    setPreviousRecommendation("");
    setViewingPrevious(false);
    setVintageReport(null);
    setShowVintageReport(false);
    setError("");
    resetFeedback();
    setSessionSuggestedWines([]);
    // Starting fresh means abandoning any unrated recommendation from a prior visit —
    // otherwise it keeps sitting there stale after the wine list itself has been cleared.
    setIsResumedPending(false);
    setPendingDateLabel("");
    updatePendingRecommendation(null);
  }

  function handleAnotherBottle() {
    resetFeedback();
    // Re-run recommendation with existing wine list
    getRecommendationWithList(wineList);
  }

  const handleScanImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Scanning while a resumed (prior-visit) recommendation is showing means the person
    // is starting something new, not continuing that old list — reset first so the new
    // scan doesn't get appended onto stale text from a different day.
    if (isResumedPending) {
      handleNewWineList();
    }

    setScanning(true);
    setScanError("");
    setScanMessage(SCAN_MESSAGES[0]);
    setScanDots(".");

    let msgIndex = 0;
    let dotCount = 1;
    const msgInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % SCAN_MESSAGES.length;
      setScanMessage(SCAN_MESSAGES[msgIndex]);
    }, 2000);
    const dotInterval = setInterval(() => {
      dotCount = dotCount >= 3 ? 1 : dotCount + 1;
      setScanDots(".".repeat(dotCount));
    }, 500);

    try {
      const { imageBase64, mediaType } = await compressImage(file);

      let wineListText;
      let lastFailureReason = null;
      try {
        wineListText = await attemptWineListFetch(imageBase64, mediaType, sessionRef.current?.user?.id);
      } catch (firstErr) {
        // One quiet automatic retry — most failures at this step are a single dropped
        // request (flaky restaurant wifi, a cold Railway instance) rather than anything
        // wrong with the image, so it's worth trying again before bothering the user.
        lastFailureReason = firstErr.message;
        await new Promise(resolve => setTimeout(resolve, 1200));
        try {
          wineListText = await attemptWineListFetch(imageBase64, mediaType, sessionRef.current?.user?.id);
        } catch (secondErr) {
          lastFailureReason = secondErr.message;
        }
      }

      if (wineListText) {
        // Tag each scan with its photo number so the recommendation can say which
        // photo a wine came from — helps the user find it again to show the server.
        setWineList(prev => {
          const photoNumber = (prev.match(/^\[Photo \d+\]$/gm) || []).length + 1;
          const block = `[Photo ${photoNumber}]\n${wineListText.trim()}`;
          return prev.trim() ? prev.trim() + "\n\n" + block : block;
        });
        setScanError("");
        logUsageEvent(sessionRef.current?.user?.id, "wine_list_scan", {
          retried: lastFailureReason !== null
        });
      } else {
        setScanError("Couldn't read the wine list. Try again or type it in.");
        logUsageEvent(sessionRef.current?.user?.id, "wine_list_scan_failed", {
          reason: lastFailureReason
        });
      }
    } catch (err) {
      // Failure in compressImage itself (before any network call) — nothing to retry.
      setScanError("Couldn't connect. Try again or type the list in.");
      logUsageEvent(sessionRef.current?.user?.id, "wine_list_scan_failed", {
        reason: "compress_image"
      });
    } finally {
      clearInterval(msgInterval);
      clearInterval(dotInterval);
      setScanning(false);
    }

    // Reset file input so the same image can be re-scanned if needed
    e.target.value = "";
  };

  const getRecommendationWithList = async (listText) => {
    if (!listText.trim()) return;

    setLoading(true);
    if (recommendation) setPreviousRecommendation(recommendation);
    setViewingPrevious(false);
    setRecommendation("");
    setVintageReport(null);
    setShowVintageReport(false);
    setError("");

    let messageIndex = 0;
    let dotCount = 1;
    setLoadingMessage(LOADING_MESSAGES[0]);
    setLoadingDots(".");
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[messageIndex]);
    }, 2000);
    const dotsInterval = setInterval(() => {
      dotCount = dotCount >= 3 ? 1 : dotCount + 1;
      setLoadingDots(".".repeat(dotCount));
    }, 500);

    try {
      const response = await apiFetch("/recommend", {
          profile: profile,
          wineList: listText.trim(),
          sessionContext: sessionContext.trim() || "Open to anything on this list.",
          alreadySuggested: sessionSuggestedWines,
          companions
        });

      const data = await response.json();
      logClaudeUsage(sessionRef.current?.user?.id, "recommend", data);

      if (data.recommendation) {
        setRecommendation(data.recommendation);
        setRecCompanionNames(companions.map(c => c.name));
        setFeedbackStep("pick");
        setIsResumedPending(false);
        // Accumulate newly suggested wines for this session
        const newWines = parseRecommendedWines(data.recommendation);
        setSessionSuggestedWines(prev => {
          const existing = new Set(prev);
          return [...prev, ...newWines.filter(w => !existing.has(w))];
        });
        logUsageEvent(sessionRef.current?.user?.id, "recommendation", {
          sessionContext: sessionContext.trim() || null,
          companions: companions.length
        });
        // Remember this recommendation as unrated until feedback is captured or dismissed —
        // this is what lets the rating prompt survive a reload/backgrounding and resume later.
        updatePendingRecommendation({
          recommendation: data.recommendation,
          sessionContext: sessionContext.trim() || null,
          createdAt: new Date().toISOString()
        });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Couldn't connect to Davino. Make sure the backend is running.");
    } finally {
      clearInterval(messageInterval);
      clearInterval(dotsInterval);
      setLoading(false);
    }
  };

  const getRecommendation = async () => {
    resetFeedback();
    await getRecommendationWithList(wineList);
  };

  const fetchVintageReport = async () => {
    if (vintageReport) {
      setShowVintageReport(v => !v);
      return;
    }
    setVintageLoading(true);
    setShowVintageReport(true);
    setVintageError("");
    try {
      const wines = parseRecommendedWines(recommendation);
      if (!wines.length) {
        setVintageError("No wines found to assess.");
        setVintageLoading(false);
        return;
      }
      const response = await apiFetch("/vintage-report", { wines });
      const data = await response.json();
      logClaudeUsage(sessionRef.current?.user?.id, "vintage-report", data);
      if (data.vintages && data.vintages.length) {
        setVintageReport(data.vintages);
      } else if (data.error) {
        setVintageError(data.error);
      } else {
        setVintageError("No vintage data returned.");
      }
    } catch (err) {
      console.error("Vintage report error:", err);
      setVintageError("Couldn't load vintage report. Try again.");
    } finally {
      setVintageLoading(false);
    }
  };

  const recommendedWines = recommendation ? parseRecommendedWines(recommendation) : [];

  const feedbackHistory = (profile.feedback_history || []).filter(e => e.type !== "comparison");
  const filteredHistory = historyFilter === "fours"
    ? feedbackHistory.filter(e => e.rating === 4)
    : historyFilter === "good"
    ? feedbackHistory.filter(e => e.rating >= 3)
    : feedbackHistory;
  const sortedHistory = [...filteredHistory].sort((a, b) => {
    if (b.rating !== a.rating) return b.rating - a.rating; // highest rating first
    return new Date(b.date) - new Date(a.date);            // newest first within same rating
  });

  return (
    <div style={styles.wrapper}>
      <div style={styles.app}>

        {/* Header */}
        <div style={{ ...styles.header, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={styles.logo}>
              da<span style={styles.logoAccent}>vino</span>
            </div>
            <div style={styles.tagline}>Your table-side sommelier</div>
          </div>
          <button style={styles.inviteHeaderButton} onClick={openInvite} aria-label="Invite a friend">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="9" cy="8" r="4" />
              <path d="M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7" />
              <path d="M19 8v6M16 11h6" />
            </svg>
            Invite
          </button>
        </div>

        {/* Tab bar */}
        <div style={styles.tabBar}>
          <button
            style={{ ...styles.tab, ...(currentView === "recommend" ? styles.tabActive : {}) }}
            onClick={() => setCurrentView("recommend")}
          >
            Recommend
          </button>
          <button
            style={{ ...styles.tab, ...(currentView === "history" ? styles.tabActive : {}) }}
            onClick={() => setCurrentView("history")}
          >
            My Wines
          </button>
        </div>

        {/* Recommend view */}
        {currentView === "recommend" && (
          <>
            <div style={styles.section}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <label style={{ ...styles.label, marginBottom: 0 }}>Wine List</label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {!scanning && wineList.trim() && (
                    <button
                      onClick={() => {
                        handleNewWineList();
                        setScanError("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#666",
                        fontSize: "12px",
                        padding: "5px 2px",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textDecoration: "underline"
                      }}
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => fileInputRef.current.click()}
                    disabled={scanning}
                    style={{
                      background: "none",
                      border: "1px solid #3a3a3a",
                      borderRadius: "999px",
                      color: scanning ? "#666" : "#ccc",
                      fontSize: "14px",
                      fontWeight: "600",
                      padding: "7px 14px",
                      cursor: scanning ? "not-allowed" : "pointer",
                      fontFamily: "inherit",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {scanning ? `${scanMessage}${scanDots}` : wineList.trim() ? "📷 Scan another" : "📷 Scan a list or bottle(s)"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleScanImage}
                    style={{ display: "none" }}
                  />
                </div>
              </div>
              {scanError && (
                <div style={{ color: "#ff6b6b", fontSize: "13px", marginBottom: "8px" }}>{scanError}</div>
              )}
              <textarea
                style={styles.textarea}
                placeholder={"Paste the wine list here...\n\nExample:\n2022 Meursault, Burgundy, $95\n2021 Caymus Cabernet, Napa, $145"}
                value={wineList}
                onChange={e => setWineList(e.target.value)}
              />

              <DiningTogether
                userId={session?.user?.id}
                displayName={profile.display_name}
                labelStyle={styles.label}
                initialCode={pairCodeFromLink}
                onCompanionsChange={setCompanions}
                onDisplayNameSaved={(name) => setProfile(prev => {
                  if (!prev) return prev;
                  const updated = { ...prev, display_name: name };
                  try { localStorage.setItem("davino_profile_cache", JSON.stringify(updated)); } catch (e) {}
                  return updated;
                })}
                logEvent={(type, meta) => logUsageEvent(sessionRef.current?.user?.id, type, meta)}
              />

              <div style={{ marginTop: "16px" }}>
                <label style={styles.label}>Anything to keep in mind — taste or value? (optional)</label>
                <input
                  style={styles.contextInput}
                  placeholder='e.g. "going with steak" or "keep it under $80"'
                  value={sessionContext}
                  onChange={e => setSessionContext(e.target.value)}
                />
              </div>

              <button
                style={{
                  ...styles.button,
                  ...(loading || scanning || !wineList.trim() ? styles.buttonDisabled : {})
                }}
                onClick={getRecommendation}
                disabled={loading || scanning || !wineList.trim()}
              >
                {loading ? "Finding your wines..." : scanning ? "Scanning wine list..." : recommendation ? "Update Recommendation" : "Get Recommendation"}
              </button>
            </div>

            {loading && (
              <div style={styles.loadingText}>{loadingMessage}{loadingDots}</div>
            )}

            {error && (
              <div style={styles.error}>{error}</div>
            )}

            {recommendation && !loading && (
              <>
                <div style={styles.divider} />
                <div style={styles.recommendationBox}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <div style={styles.recommendationTitle}>
                      {viewingPrevious ? "Previous picks" : isResumedPending ? `From ${pendingDateLabel}` : recCompanionNames.length ? `Picks for you & ${recCompanionNames.join(" & ")}` : "Tonight's picks"}
                    </div>
                    {previousRecommendation && (
                      <button
                        onClick={() => setViewingPrevious(v => !v)}
                        style={{
                          background: "none",
                          border: "1px solid #333",
                          borderRadius: "8px",
                          color: "#888",
                          fontSize: "12px",
                          padding: "4px 10px",
                          cursor: "pointer",
                          fontFamily: "inherit"
                        }}
                      >
                        {viewingPrevious ? "Current →" : "← Previous"}
                      </button>
                    )}
                  </div>
                  <div style={styles.recommendationText}>
                    <ReactMarkdown components={markdownComponents}>
                      {viewingPrevious ? previousRecommendation : recommendation}
                    </ReactMarkdown>
                  </div>
                </div>

                {/* Vintage Report */}
                {!viewingPrevious && (
                  <div style={{ margin: "0 20px 16px" }}>
                    <button
                      onClick={fetchVintageReport}
                      style={{
                        width: "100%",
                        backgroundColor: "transparent",
                        border: "1px solid #333",
                        borderRadius: "10px",
                        color: "#aaa",
                        fontSize: "14px",
                        padding: "11px",
                        cursor: "pointer",
                        fontFamily: "inherit"
                      }}
                    >
                      {vintageLoading ? "Loading vintage report..." : showVintageReport ? "Hide vintage report" : "📊 Vintage report"}
                    </button>

                    {showVintageReport && vintageError && (
                      <div style={{ marginTop: "12px", padding: "12px", backgroundColor: "#2a1a1a", borderRadius: "10px", color: "#ff6b6b", fontSize: "13px" }}>
                        {vintageError}
                      </div>
                    )}

                    {showVintageReport && vintageReport && (
                      <div style={{ marginTop: "12px", backgroundColor: "#1a1a1a", borderRadius: "12px", border: "1px solid #2a2a2a", overflow: "hidden" }}>
                        <div style={{ padding: "14px 16px 10px", fontSize: "11px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #222" }}>
                          Vintage quality by region
                        </div>
                        {vintageReport.map((v, i) => (
                          <div key={i} style={{ padding: "14px 16px", borderBottom: i < vintageReport.length - 1 ? "1px solid #1e1e1e" : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
                              <div style={{ fontSize: "13px", fontWeight: "600", color: "#e0e0e0", flex: 1, paddingRight: "12px" }}>{v.wine}</div>
                              <div style={{ fontSize: "13px", fontWeight: "700", color: "#8B4513", whiteSpace: "nowrap" }}>{v.score}</div>
                            </div>
                            <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>{v.region}</div>
                            <div style={{ fontSize: "13px", color: "#999", lineHeight: "1.5" }}>{v.note}</div>
                          </div>
                        ))}
                        <div style={{ padding: "10px 16px", fontSize: "11px", color: "#444", borderTop: "1px solid #1e1e1e" }}>
                          5/5 = benchmark · 4/5 = excellent · 3/5 = solid · below 3/5 = approach with care
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Feedback flow */}
                {feedbackStep && feedbackStep !== "done" && (
                  <div style={styles.feedbackBox}>
                    <div style={styles.feedbackTitle}>How'd it go?</div>

                    {feedbackStep === "pick" && (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                          <div style={{ ...styles.feedbackQuestion, marginBottom: 0 }}>
                            {isResumedPending ? `From ${pendingDateLabel} — did you try one of these?` : "What did you end up ordering?"}
                          </div>
                          <button
                            onClick={() => { setFeedbackStep("done"); updatePendingRecommendation(null); }}
                            style={{ background: "none", border: "none", color: "#555", fontSize: "13px", cursor: "pointer", fontFamily: "inherit", padding: "4px" }}
                          >
                            {isResumedPending ? "Didn't order any of these" : "Skip"}
                          </button>
                        </div>
                        {recommendedWines.map((wine, i) => (
                          <button
                            key={i}
                            style={styles.wineOptionButton}
                            onClick={() => handleWinePick(wine)}
                          >
                            {wine}
                          </button>
                        ))}
                        {!showSomethingElse ? (
                          <button
                            style={styles.somethingElseButton}
                            onClick={() => setShowSomethingElse(true)}
                          >
                            Something else...
                          </button>
                        ) : (
                          <div>
                            <input
                              style={{ ...styles.feedbackNoteInput, marginBottom: "8px" }}
                              placeholder="What did you order?"
                              value={somethingElseText}
                              onChange={e => setSomethingElseText(e.target.value)}
                              autoFocus
                            />
                            <button
                              style={{
                                ...styles.feedbackSubmitButton,
                                opacity: somethingElseText.trim() ? 1 : 0.4
                              }}
                              onClick={handleSomethingElseSubmit}
                              disabled={!somethingElseText.trim()}
                            >
                              Continue
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {feedbackStep === "rate" && (
                      <>
                        <div style={styles.feedbackQuestion}>How was the {feedbackWine.split("—")[0].trim()}?</div>
                        {RATING_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            style={styles.ratingButton}
                            onClick={() => handleRating(opt.value)}
                          >
                            <span style={styles.ratingNumber}>{opt.value}</span>
                            <span>{opt.label}</span>
                          </button>
                        ))}
                      </>
                    )}

                    {feedbackStep === "note" && (
                      <>
                        <div style={styles.feedbackQuestion}>Anything to remember about it?</div>
                        <input
                          style={styles.feedbackNoteInput}
                          placeholder="Venue, occasion, food pairing... (optional)"
                          value={feedbackNote}
                          onChange={e => setFeedbackNote(e.target.value)}
                        />
                        <button
                          style={styles.feedbackSubmitButton}
                          onClick={handleFeedbackSubmit}
                        >
                          Save
                        </button>
                      </>
                    )}

                    {feedbackStep === "compare" && comparisonCandidate && (
                      <>
                        <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
                          Comparing wines helps Davino sharpen your taste profile over time.
                        </div>
                        <div style={styles.feedbackQuestion}>Which did you prefer?</div>
                        <button
                          style={styles.wineOptionButton}
                          onClick={() => handleComparisonChoice(feedbackWine, comparisonCandidate.wine)}
                        >
                          {feedbackWine.split("—")[0].trim()} — tonight
                        </button>
                        <button
                          style={styles.wineOptionButton}
                          onClick={() => handleComparisonChoice(comparisonCandidate.wine, feedbackWine)}
                        >
                          {comparisonCandidate.wine.split("—")[0].trim()}
                        </button>
                        <button
                          style={styles.somethingElseButton}
                          onClick={() => setFeedbackStep("done")}
                        >
                          Too hard to say
                        </button>
                      </>
                    )}
                  </div>
                )}

                {feedbackStep === "done" && (
                  <div style={styles.feedbackBox}>
                    <div style={styles.feedbackDone}>Noted. Davino will remember that. 🍷</div>
                    {feedbackRating === 4 && (
                      <button style={styles.inviteNudge} onClick={openInvite}>
                        Know someone who'd enjoy Davino? <span style={{ color: "#c4834f" }}>Invite them</span>
                      </button>
                    )}
                    {wineList.trim() && (
                      <button
                        style={styles.nextStepPrimary}
                        onClick={handleAnotherBottle}
                      >
                        Recommend another bottle
                      </button>
                    )}
                    <button
                      style={styles.nextStepSecondary}
                      onClick={() => fileInputRef.current.click()}
                    >
                      📷 Scan a list or bottle(s)
                    </button>
                    <button
                      style={styles.nextStepSecondary}
                      onClick={handleNewWineList}
                    >
                      New wine list
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* My Wines view */}
        {currentView === "history" && (
          <div style={styles.historySection}>

            {/* Log a wine flow */}
            {!logWineStep && (
              <button
                onClick={() => setLogWineStep("name")}
                style={{
                  width: "100%",
                  backgroundColor: "transparent",
                  border: "1px solid #333",
                  borderRadius: "10px",
                  color: "#aaa",
                  fontSize: "15px",
                  padding: "13px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  marginBottom: "20px"
                }}
              >
                + Log a wine
              </button>
            )}

            {logWineStep === "name" && (
              <div style={{ ...styles.feedbackBox, marginBottom: "20px", margin: "0 0 20px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <div style={styles.feedbackTitle}>Log a wine</div>
                  <button
                    onClick={() => logWineFileInputRef.current.click()}
                    disabled={logWineScanning}
                    style={{
                      background: "none",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      color: logWineScanning ? "#555" : "#aaa",
                      fontSize: "12px",
                      padding: "5px 10px",
                      cursor: logWineScanning ? "not-allowed" : "pointer",
                      fontFamily: "inherit"
                    }}
                  >
                    {logWineScanning ? "Scanning..." : "📷 Scan label"}
                  </button>
                  <input
                    ref={logWineFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogWineScan}
                    style={{ display: "none" }}
                  />
                </div>
                <div style={styles.feedbackQuestion}>What wine did you try?</div>
                {logWineScanError && (
                  <div style={{ color: "#ff6b6b", fontSize: "13px", marginBottom: "8px" }}>{logWineScanError}</div>
                )}
                <input
                  style={styles.feedbackNoteInput}
                  placeholder="e.g. 2019 Durand Cornas, Château Margaux..."
                  value={logWineName}
                  onChange={e => setLogWineName(e.target.value)}
                  autoFocus
                />
                <button
                  style={{ ...styles.feedbackSubmitButton, opacity: logWineName.trim() ? 1 : 0.4 }}
                  onClick={() => logWineName.trim() && setLogWineStep("rate")}
                  disabled={!logWineName.trim()}
                >
                  Continue
                </button>
                <button onClick={resetLogWine} style={{ ...styles.somethingElseButton, marginTop: "8px" }}>
                  Cancel
                </button>
              </div>
            )}

            {logWineStep === "rate" && (
              <div style={{ ...styles.feedbackBox, marginBottom: "20px", margin: "0 0 20px 0" }}>
                <div style={styles.feedbackTitle}>Log a wine</div>
                <div style={styles.feedbackQuestion}>How was the {logWineName.split("—")[0].trim()}?</div>
                {RATING_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    style={styles.ratingButton}
                    onClick={() => { setLogWineRating(opt.value); setLogWineStep("note"); }}
                  >
                    <span style={styles.ratingNumber}>{opt.value}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
                <button onClick={resetLogWine} style={{ ...styles.somethingElseButton, marginTop: "4px" }}>
                  Cancel
                </button>
              </div>
            )}

            {logWineStep === "note" && (
              <div style={{ ...styles.feedbackBox, marginBottom: "20px", margin: "0 0 20px 0" }}>
                <div style={styles.feedbackTitle}>Log a wine</div>
                <div style={styles.feedbackQuestion}>Anything to remember about it?</div>
                <input
                  style={styles.feedbackNoteInput}
                  placeholder="Venue, occasion, food pairing... (optional)"
                  value={logWineNote}
                  onChange={e => setLogWineNote(e.target.value)}
                />
                <button style={styles.feedbackSubmitButton} onClick={handleLogWineSave}>
                  Save
                </button>
                <button onClick={resetLogWine} style={{ ...styles.somethingElseButton, marginTop: "8px" }}>
                  Cancel
                </button>
              </div>
            )}

            {logWineStep === "saved" && (
              <div style={{ ...styles.feedbackBox, marginBottom: "20px", margin: "0 0 20px 0" }}>
                <div style={styles.feedbackDone}>Noted. Davino will remember that. 🍷</div>
                {logWineRating === 4 && (
                  <button style={styles.inviteNudge} onClick={openInvite}>
                    Know someone who'd enjoy Davino? <span style={{ color: "#c4834f" }}>Invite them</span>
                  </button>
                )}
                <button style={styles.feedbackSubmitButton} onClick={resetLogWine}>
                  Log another
                </button>
              </div>
            )}

            {/* Wine list */}
            {feedbackHistory.length === 0 ? (
              <div style={styles.historyEmpty}>
                Your rated wines will appear here.<br />
                Rate wines after a recommendation, or log one directly above.
              </div>
            ) : (
              <>
                <div style={styles.historyToggle}>
                  <button
                    style={{ ...styles.historyToggleBtn, ...(historyFilter === "fours" ? styles.historyToggleBtnActive : {}) }}
                    onClick={() => setHistoryFilter("fours")}
                  >
                    Seeking out
                  </button>
                  <button
                    style={{ ...styles.historyToggleBtn, ...(historyFilter === "good" ? styles.historyToggleBtnActive : {}) }}
                    onClick={() => setHistoryFilter("good")}
                  >
                    Good & above
                  </button>
                  <button
                    style={{ ...styles.historyToggleBtn, ...(historyFilter === "all" ? styles.historyToggleBtnActive : {}) }}
                    onClick={() => setHistoryFilter("all")}
                  >
                    All
                  </button>
                </div>

                {sortedHistory.length === 0 ? (
                  <div style={styles.historyEmpty}>
                    No wines in this category yet.
                  </div>
                ) : (
                  sortedHistory.map((entry, i) => (
                    <div key={i} style={styles.historyCard}>
                      <div style={styles.historyWineName}>{entry.wine}</div>
                      <div style={styles.historyMeta}>
                        <span style={styles.historyRating}>{entry.rating}</span>
                        <span style={styles.historyRatingLabel}>
                          {RATING_OPTIONS.find(r => r.value === entry.rating)?.label}
                        </span>
                        <span style={styles.historyDate}>{formatHistoryDate(entry.date)}</span>
                      </div>
                      {entry.note && (
                        <div style={styles.historyNote}>"{entry.note}"</div>
                      )}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", padding: "24px 20px 40px", display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={openInvite}
            style={{
              fontSize: "15px",
              color: "#aaa",
              background: "none",
              border: "1px solid #444",
              borderRadius: "10px",
              padding: "10px 20px",
              display: "inline-block",
              cursor: "pointer",
              fontFamily: "inherit"
            }}
          >
            Invite a friend
          </button>
          <a
            href="mailto:feedback@davino.ai?subject=Davino feedback"
            style={{
              fontSize: "15px",
              color: "#aaa",
              textDecoration: "none",
              border: "1px solid #444",
              borderRadius: "10px",
              padding: "10px 20px",
              display: "inline-block"
            }}
          >
            Please send feedback
          </a>
        </div>

      </div>

      {showInvite && (
        <InviteSheet
          onClose={() => setShowInvite(false)}
          onShare={() => logUsageEvent(sessionRef.current?.user?.id, "invite_link_shared")}
        />
      )}

    </div>
  );
}
